import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import BrandLogo from "@/components/BrandLogo";
import SyncChip from "@/components/SyncChip";
import { CheckCircle, Truck, LockKey, CloudArrowUp, CaretLeft } from "@phosphor-icons/react";
import OfflineIndicator from "@/components/OfflineIndicator";
import StaleDataBanner from "@/components/StaleDataBanner";
import InstallPrompt from "@/components/InstallPrompt";
import { queueSubmission } from "@/lib/outbox";
import { cachePut, cacheGetVerified } from "@/lib/refcache";

export default function DocketCapture() {
  const [params] = useSearchParams();
  const assetId = params.get("asset");
  const navigate = useNavigate();

  const [pin, setPin] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [alloc, setAlloc] = useState(null);
  const [downtimeReasons, setDowntimeReasons] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [projTasks, setProjTasks] = useState([]);
  const [taskRows, setTaskRows] = useState([]);
  const [done, setDone] = useState(false);
  const [doneHours, setDoneHours] = useState(0);
  const [queuedOffline, setQueuedOffline] = useState(false);
  const [staleAt, setStaleAt] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    asset_id: assetId || "", asset_no: "", supplier: "", project_id: "", assignment_id: "",
    date: today, shift: "Day", start_time: "07:00", finish_time: "15:00",
    down_time_hours: 0, down_time_reason: "", machine_start: "", machine_finish: "", notes: "",
  });
  const [opName, setOpName] = useState("");
  const [opSelect, setOpSelect] = useState("");
  const [opSig, setOpSig] = useState("");
  const [repName, setRepName] = useState("");
  const [repSig, setRepSig] = useState("");
  const [repUnavailable, setRepUnavailable] = useState(false);
  const [repReason, setRepReason] = useState("");

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const savedWorker = (() => {
    try { return JSON.parse(localStorage.getItem("eq_field_worker") || "null"); } catch { return null; }
  })();
  const draftKey = assetId ? `eq_docket_draft:${assetId}` : null;
  const [resumedAt, setResumedAt] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);

  const exitToHub = () => navigate("/field");

  const saveDraft = () => {
    if (!draftKey) return;
    const draft = {
      form, taskRows, opName, opSelect, opSig, repName, repSig, repUnavailable, repReason,
      savedAt: new Date().toISOString(),
    };
    try { localStorage.setItem(draftKey, JSON.stringify(draft)); } catch { /* quota */ }
    setDraftSaved(true);
    setTimeout(() => exitToHub(), 700);
  };

  const discardDraft = () => {
    if (draftKey) localStorage.removeItem(draftKey);
    setResumedAt(null);
    window.location.reload();
  };

  const switchProject = (pid) => {
    const a = (alloc?.allocations || []).find((x) => x.project_id === pid);
    if (!a) return;
    const meta = (alloc?.projects_meta || {})[pid] || { tasks: [], workers: [] };
    setProjTasks(meta.tasks || []);
    setWorkers(meta.workers || []);
    setTaskRows([]);
    setForm((s) => ({ ...s, project_id: a.project_id, assignment_id: a.assignment_id,
                     supplier: a.supplier || alloc?.asset?.supplier || "" }));
  };

  const applyData = (data, prefillName) => {
    setAlloc(data);
    setDowntimeReasons(data.downtime_reasons || []);
    setWorkers(data.workers || []);
    setProjTasks(data.tasks || []);
    setForm((s) => ({
      ...s, asset_id: data.asset.id, asset_no: data.asset.asset_no,
      supplier: data.allocation?.supplier || data.asset.supplier || "",
      project_id: data.allocation?.project_id || "", assignment_id: data.allocation?.assignment_id || "",
    }));
    if (prefillName) {
      const inList = (data.workers || []).some((w) => w.name === prefillName);
      setOpSelect(inList ? prefillName : "__other__");
      setOpName(prefillName);
    }
    // Resume a locally-saved draft for this asset (e.g. walked off to get a signature).
    if (draftKey) {
      try {
        const raw = localStorage.getItem(draftKey);
        if (raw) {
          const dft = JSON.parse(raw);
          if (dft.form) setForm((s) => ({ ...s, ...dft.form }));
          if (dft.taskRows) setTaskRows(dft.taskRows);
          if (dft.opName != null) setOpName(dft.opName);
          if (dft.opSelect != null) setOpSelect(dft.opSelect);
          if (dft.opSig != null) setOpSig(dft.opSig);
          if (dft.repName != null) setRepName(dft.repName);
          if (dft.repSig != null) setRepSig(dft.repSig);
          if (dft.repUnavailable != null) setRepUnavailable(dft.repUnavailable);
          if (dft.repReason != null) setRepReason(dft.repReason);
          setResumedAt(dft.savedAt || true);
        }
      } catch { /* ignore */ }
    }
    setUnlocked(true);
  };

  const unlock = async () => {
    setError("");
    if (!assetId) { setError("Missing asset — please scan the asset's QR code."); return; }
    if (!pin) { setError("Enter the asset PIN."); return; }
    setVerifying(true);
    try {
      const { data } = await api.get(`/public/asset-docket/${assetId}`, { params: { pin } });
      applyData(data);
      cachePut(`docket:${assetId}`, data, pin).catch(() => {});
    } catch (e) {
      if (!e.response) {
        const hit = await cacheGetVerified(`docket:${assetId}`, pin).catch(() => null);
        if (hit) { applyData(hit.data); setStaleAt(hit.cached_at); }
        else setError("You're offline and this asset hasn't been opened on this device before (or the PIN doesn't match). Connect once to enable offline use.");
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setVerifying(false);
  };

  // Identified worker rostered on this asset's project → open the docket without the asset PIN.
  useEffect(() => {
    if (!assetId || !savedWorker?.id || !savedWorker?.token) return;
    (async () => {
      setVerifying(true);
      try {
        const { data } = await api.get(`/public/asset-docket/${assetId}`,
          { params: { worker_id: savedWorker.id, token: savedWorker.token } });
        applyData(data, savedWorker.name);
        cachePut(`docket:${assetId}`, data, null).catch(() => {});
      } catch (e) { /* not rostered / offline → fall back to the PIN gate */ }
      setVerifying(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId]);


  useEffect(() => { if (!assetId) setError("No asset selected. Scan an asset QR code to begin."); }, [assetId]);

  const workedPreview = (() => {
    const hm = (t) => { const [h, m] = (t || "").split(":"); return h != null ? +h + (+m || 0) / 60 : null; };
    const a = hm(form.start_time), b = hm(form.finish_time);
    if (a == null || b == null) return 0;
    let d = b - a; if (d < 0) d += 24;
    return Math.max(0, d - (Number(form.down_time_hours) || 0)).toFixed(2);
  })();

  const submit = async () => {
    setError("");
    if (!form.asset_no) { setError("Please select the asset."); return; }
    if (!opName || !opSig) { setError("Worker name & signature required."); return; }
    if (form.machine_start !== "" && form.machine_finish !== "" && Number(form.machine_finish) < Number(form.machine_start)) {
      setError("SMU at Finish cannot be less than SMU at Start."); return;
    }
    for (const r of taskRows) {
      const nm = r.task_id === "__manual__" ? (r.name || "").trim() : r.task_id;
      if (!nm) { setError("Each task row needs a task selected (or a name typed)."); return; }
      if (!(Number(r.hours) > 0)) { setError("Enter the hours spent on each task."); return; }
    }
    const taskTotal = taskRows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
    if (taskRows.length && taskTotal > Number(workedPreview) + 0.01) {
      setError(`Task hours (${taskTotal}) can't exceed worked hours (${workedPreview}).`); return;
    }
    const task_entries = taskRows.map((r) => {
      if (r.task_id === "__manual__") return { task_id: null, code: "", name: r.name.trim(), hours: Number(r.hours), manual: true };
      const t = projTasks.find((x) => x.id === r.task_id) || {};
      return { task_id: r.task_id, code: t.code || "", name: t.name || "", hours: Number(r.hours), manual: false };
    });
    setSaving(true);
    const payload = {
      ...form, pin: pin || undefined,
      worker_id: savedWorker?.id, field_token: savedWorker?.token,
      task_entries,
      down_time_hours: Number(form.down_time_hours) || 0,
      machine_start: form.machine_start === "" ? null : Number(form.machine_start),
      machine_finish: form.machine_finish === "" ? null : Number(form.machine_finish),
      operator_name: opName, operator_sig: opSig,
      rep_name: repName, rep_sig: repSig, rep_unavailable: repUnavailable, rep_reason: repReason,
      client_ref: crypto.randomUUID(),
      captured_at: new Date().toISOString(),
    };
    try {
      const { data } = await api.post("/public/dockets", payload);
      if (draftKey) localStorage.removeItem(draftKey);
      setDoneHours(data.worked_hours);
      setDone(true);
    } catch (e) {
      if (!e.response) {
        await queueSubmission("docket", "/public/dockets", payload);
        if (draftKey) localStorage.removeItem(draftKey);
        setQueuedOffline(true);
      } else {
        setError(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setSaving(false);
  };

  const inp = "w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-[16px] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)] disabled:bg-surface-inset disabled:text-ink-disabled";
  const lbl = "text-[10.5px] font-bold uppercase tracking-[0.1em] text-ink-muted mb-1.5 block";

  if (queuedOffline) {
    return (
      <div className="min-h-screen bg-equipr-black flex items-center justify-center p-6">
        <div className="bg-white w-full max-w-sm p-8 text-center" data-testid="docket-queued">
          <CloudArrowUp size={56} weight="fill" className="text-amber-500 mx-auto" />
          <h2 className="text-xl font-bold text-equipr-black mt-3">Saved on this device</h2>
          <p className="text-sm text-gray-500 mt-1">No coverage right now — your docket will send automatically when you're back in signal. Keep this page installed / open.</p>
          <div className="flex gap-2 mt-6">
            <button onClick={exitToHub} data-testid="docket-queued-hub" className="flex-1 h-10 border border-gray-300 text-equipr-black font-bold rounded-none hover:bg-gray-50">Back to Home</button>
            <button onClick={() => window.location.reload()} data-testid="docket-queued-new" className="flex-1 h-10 bg-equipr-yellow text-equipr-black font-bold rounded-none">New Docket</button>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-equipr-black flex items-center justify-center p-6">
        <div className="bg-white w-full max-w-sm p-8 text-center" data-testid="docket-success">
          <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto" />
          <h2 className="text-xl font-bold text-equipr-black mt-3">Docket submitted</h2>
          <p className="text-sm text-gray-500 mt-1">Worked hours: <b>{doneHours}</b> · sent for approval.</p>
          <div className="flex gap-2 mt-6">
            <button onClick={exitToHub} data-testid="docket-done-hub" className="flex-1 h-10 border border-gray-300 text-equipr-black font-bold rounded-none hover:bg-gray-50">Back to Home</button>
            <button onClick={() => window.location.reload()} data-testid="docket-new-btn" className="flex-1 h-10 bg-equipr-yellow text-equipr-black font-bold rounded-none">New Docket</button>
          </div>
        </div>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-equipr-black flex flex-col">
        <InstallPrompt />
        <div className="px-4 py-3 flex items-center justify-between"><BrandLogo variant="dark" showProduct={false} /><SyncChip /></div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white w-full max-w-sm p-8" data-testid="docket-pin-gate">
            <div className="flex items-center gap-2 text-equipr-black mb-1">
              <LockKey size={22} weight="fill" className="text-accent-ink" />
              <h2 className="text-lg font-bold">Hire Docket</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">Enter the asset PIN to record today's working times.</p>
            {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 mb-3" data-testid="docket-pin-error">{error}</div>}
            <label className={lbl}>Asset PIN</label>
            <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric" placeholder="••••••" data-testid="docket-pin-input"
              onKeyDown={(e) => e.key === "Enter" && unlock()}
              className="w-full h-[54px] px-[15px] border-[1.5px] border-border-control bg-white text-center text-2xl tracking-[0.5em] rounded-[12px] focus:border-ink-primary focus:outline-none focus:shadow-[0_0_0_3px_rgba(255,209,8,0.35)]" />
            <button onClick={unlock} disabled={verifying || !assetId} data-testid="docket-pin-submit"
              className="w-full h-[54px] mt-5 bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
              {verifying ? "Checking…" : "Unlock Docket"}
            </button>
            <button onClick={exitToHub} data-testid="docket-pin-cancel"
              className="w-full h-11 mt-3 text-[13px] font-bold text-gray-500 hover:text-equipr-black">Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <OfflineIndicator />
      <StaleDataBanner cachedAt={staleAt} />
      <InstallPrompt />
      <div className="bg-equipr-black text-white px-4 py-3 flex items-center justify-between">
        <button onClick={exitToHub} data-testid="docket-cancel" className="flex items-center gap-1 text-[13px] font-semibold text-gray-300 hover:text-white"><CaretLeft size={16} weight="bold" /> Exit</button>
        <span className="flex items-center gap-2"><SyncChip /><span className="text-sm font-semibold text-equipr-yellow">Hire Docket</span></span>
      </div>
      <div className="max-w-md mx-auto p-4 space-y-4">
        {error && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="docket-error">{error}</div>}
        {resumedAt && (
          <div className="bg-blue-50 border border-blue-200 text-blue-800 text-[13px] px-3 py-2 flex items-center justify-between gap-2" data-testid="docket-resumed">
            <span>Resumed your saved docket{typeof resumedAt === "string" ? ` · saved ${new Date(resumedAt).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}` : ""}.</span>
            <button onClick={discardDraft} data-testid="docket-discard-draft" className="font-bold underline shrink-0">Discard</button>
          </div>
        )}

        {/* Asset */}
        <div className="bg-white border border-gray-200 p-4">
          <label className={lbl}>Asset</label>
          <div className="flex items-center gap-2 text-sm font-semibold text-equipr-black" data-testid="docket-asset-fixed">
            <Truck size={18} /> {form.asset_no || "…"} {alloc?.asset?.asset_type ? `· ${alloc.asset.asset_type}` : ""}
          </div>
          {(alloc?.allocations || []).length > 1 ? (
            <div className="mt-2" data-testid="docket-project-switch">
              <label className="text-[11px] text-gray-500">This asset is on {alloc.allocations.length} live jobs — confirm which one:</label>
              <select className={inp + " mt-1"} value={form.project_id} onChange={(e) => switchProject(e.target.value)} data-testid="docket-project-select">
                {alloc.allocations.map((a) => <option key={a.assignment_id} value={a.project_id}>{a.project_name}</option>)}
              </select>
            </div>
          ) : (
            alloc?.allocation?.project_name && <p className="text-[12px] text-gray-500 mt-1" data-testid="docket-project-name">Project: {alloc.allocation.project_name}</p>
          )}
        </div>

        {/* Times */}
        <div className="bg-white border border-gray-200 p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Date *</label><input type="date" className={inp} value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="docket-date" /></div>
            <div><label className={lbl}>Shift</label>
              <select className={inp} value={form.shift} onChange={(e) => set("shift", e.target.value)}><option>Day</option><option>Night</option></select></div>
            <div><label className={lbl}>Start Time *</label><input type="time" className={inp} value={form.start_time} onChange={(e) => set("start_time", e.target.value)} data-testid="docket-start" /></div>
            <div><label className={lbl}>Finish Time *</label><input type="time" className={inp} value={form.finish_time} onChange={(e) => set("finish_time", e.target.value)} data-testid="docket-finish" /></div>
            <div><label className={lbl}>Down Time (hrs) *</label><input type="number" step="0.25" className={inp} value={form.down_time_hours} onChange={(e) => set("down_time_hours", e.target.value)} data-testid="docket-downtime" /></div>
            <div><label className={lbl}>Down Time Reason</label>
              <select className={inp} value={form.down_time_reason} onChange={(e) => set("down_time_reason", e.target.value)} data-testid="docket-downtime-reason">
                <option value="">—</option>{downtimeReasons.map((r) => <option key={r}>{r}</option>)}
              </select></div>
            <div><label className={lbl}>SMU at Start</label>
              <input type="number" step="0.1" inputMode="decimal" placeholder="Optional" className={inp} value={form.machine_start} onChange={(e) => set("machine_start", e.target.value)} data-testid="docket-smu-start" /></div>
            <div><label className={lbl}>SMU at Finish</label>
              <input type="number" step="0.1" inputMode="decimal" placeholder="Optional" className={inp} value={form.machine_finish} onChange={(e) => set("machine_finish", e.target.value)} data-testid="docket-smu-finish" /></div>
          </div>
          <div className="bg-equipr-yellow/20 border border-equipr-yellow px-3 py-2 text-sm font-bold text-equipr-black" data-testid="docket-worked-preview">
            Worked Hours: {workedPreview}
          </div>
          <div><label className={lbl}>Notes</label><textarea className={inp + " h-16 py-1"} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>

        {/* Tasks / Activities */}
        <div className="bg-white border border-gray-200 p-4 space-y-3" data-testid="docket-tasks-card">
          <div className="flex items-center justify-between">
            <label className={lbl + " mb-0"}>Tasks / Activities <span className="font-normal text-gray-400">(optional)</span></label>
            <button onClick={() => setTaskRows((r) => [...r, { task_id: "", name: "", hours: "" }])} data-testid="docket-task-add"
              className="text-[12px] font-bold text-accent-ink hover:underline">+ Add task</button>
          </div>
          {taskRows.length === 0 && (
            <p className="text-[12px] text-gray-400" data-testid="docket-tasks-empty">Record what you worked on today — tap “+ Add task”.</p>
          )}
          {taskRows.map((r, i) => (
            <div key={i} className="border border-gray-100 p-2 space-y-2" data-testid={`docket-task-row-${i}`}>
              <div className="flex gap-2">
                <select className="flex-1 min-w-0 h-10 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" value={r.task_id} data-testid={`docket-task-select-${i}`}
                  onChange={(e) => setTaskRows((rows) => rows.map((x, j) => j === i ? { ...x, task_id: e.target.value } : x))}>
                  <option value="">— Select task —</option>
                  {projTasks.map((t) => <option key={t.id} value={t.id}>{t.code ? `${t.code} — ` : ""}{t.name}</option>)}
                  <option value="__manual__">Other / enter manually…</option>
                </select>
                <input type="number" step="0.25" min="0" inputMode="decimal" placeholder="Hrs" data-testid={`docket-task-hours-${i}`}
                  className="w-20 shrink-0 h-10 px-2 border border-border-control bg-white text-[13.5px] rounded-[8px] focus:border-ink-primary focus:ring-2 focus:ring-equipr-yellow/40 focus:outline-none" value={r.hours}
                  onChange={(e) => setTaskRows((rows) => rows.map((x, j) => j === i ? { ...x, hours: e.target.value } : x))} />
                <button onClick={() => setTaskRows((rows) => rows.filter((_, j) => j !== i))} data-testid={`docket-task-remove-${i}`}
                  className="text-gray-400 hover:text-red-600 px-1 text-lg leading-none">×</button>
              </div>
              {r.task_id === "__manual__" && (
                <input className={inp} placeholder="Describe the task / activity" data-testid={`docket-task-manual-${i}`} value={r.name}
                  onChange={(e) => setTaskRows((rows) => rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} />
              )}
            </div>
          ))}
          {taskRows.length > 0 && (
            <div className="text-[12px] text-gray-500" data-testid="docket-task-total">
              Task hours total: <b>{taskRows.reduce((s, r) => s + (Number(r.hours) || 0), 0)}</b> / {workedPreview} worked
            </div>
          )}
        </div>

        {/* Signatures */}
        <div className="bg-white border border-gray-200 p-4 space-y-4">
          <div>
            <label className={lbl}>Worker Name *</label>
            <select className={inp} value={opSelect} data-testid="docket-op-select"
              onChange={(e) => { const v = e.target.value; setOpSelect(v); setOpName(v === "__other__" ? "" : v); }}>
              <option value="">— Select your name —</option>
              {workers.length > 0 && (
                <optgroup label="On this project">
                  {workers.map((w) => <option key={w.name} value={w.name}>{w.name}</option>)}
                </optgroup>
              )}
              <option value="__other__">Other / not listed…</option>
            </select>
            {opSelect === "__other__" && (
              <input className={inp + " mt-2"} value={opName} onChange={(e) => setOpName(e.target.value)} placeholder="Type your name" data-testid="docket-op-name" />
            )}
            <div className="mt-2"><SignaturePad onChange={setOpSig} testid="docket-op-sig" /></div>
          </div>
          <div className="border-t border-gray-100 pt-3">
            <div className="flex items-center justify-between">
              <label className={lbl}>Site Rep</label>
              <label className="flex items-center gap-1 text-[12px] text-gray-600">
                <input type="checkbox" checked={repUnavailable} onChange={(e) => setRepUnavailable(e.target.checked)} data-testid="docket-rep-unavailable" /> Rep unavailable
              </label>
            </div>
            <input className={inp} value={repName} onChange={(e) => setRepName(e.target.value)} placeholder="Site rep name" data-testid="docket-rep-name" />
            {repUnavailable ? (
              <input className={inp + " mt-2"} value={repReason} onChange={(e) => setRepReason(e.target.value)} placeholder="Reason rep unavailable (sign later)" data-testid="docket-rep-reason" />
            ) : (
              <div className="mt-2"><SignaturePad onChange={setRepSig} testid="docket-rep-sig" /></div>
            )}
          </div>
        </div>

        <button onClick={submit} disabled={saving} data-testid="docket-submit"
          className="w-full h-[54px] bg-equipr-yellow text-equipr-black font-bold text-[16px] uppercase tracking-[0.04em] rounded-[12px] border border-accent-edge hover:bg-equipr-yellow-dark active:translate-y-px disabled:opacity-60">
          {saving ? "Submitting…" : "Submit Docket"}
        </button>
        <button onClick={saveDraft} disabled={saving} data-testid="docket-save-later"
          className="w-full h-12 -mt-1 bg-white border-[1.5px] border-border-control text-equipr-black font-bold text-[14px] rounded-[12px] hover:bg-surface-panel active:translate-y-px disabled:opacity-60">
          {draftSaved ? "Saved ✓" : "Save for later"}
        </button>
        <p className="text-center text-[11px] text-gray-400 -mt-1 pb-2">Save keeps everything on this device — handy if you need to walk off for a signature.</p>
      </div>
    </div>
  );
}
